<template>
    <form id="wt-application"
          class="main"
          method="POST"
          action="/apply"
          @submit.prevent="saveStepFive()"
          enctype="multipart/form-data">

        <!-- Step 1 -->
        <div class="step step--1 step--centered wrapper">
            <div class="step__inner">
                <h2>Join the Club</h2>
                <p>Tell us a little about yourself, your travel style and why you'd like to join the club. Our team (of real humans) will review your application and be in touch within the week!</p>
                <div class="form">

                    <div class="form__row">

                        <!-- First Name -->
                        <div class="form__field form__field--mobile-full" :class="{'has-error' : form.errors.has('first_name') }">
                            <label for="first_name" class="accessible">First Name</label>
                            <input type="text" id="first_name" name="first_name" v-model="form.first_name" placeholder="First Name*" class="required first_name">
                            <span class="error" v-text="form.errors.first_name"></span>
                        </div>

                        <!-- Last Name -->
                        <div class="form__field form__field--mobile-full" :class="{'has-error' : form.errors.has('last_name') }">
                            <label for="last_name" class="accessible">Last Name</label>
                            <input type="text" id="last_name" name="last_name" v-model="form.last_name" placeholder="Last Name*" class="required last_name">
                            <span class="error" v-text="form.errors.last_name"></span>
                        </div>
                    </div>

                    <!-- Email Address -->
                    <div class="form__row">
                        <div class="form__field form__field--mobile-full" :class="{'has-error' : form.errors.has('email') }">
                            <label for="email" class="accessible">Email</label>
                            <input type="email" id="js-abandonded-email" name="email" autocapitalize="off" v-model="form.email" placeholder="Email*" class="js-email required application-email">
                            <span class="error" v-html="form.errors.email"></span>
                        </div>
                    </div>

                    <!-- Phone -->
                    <div class="form__row">
                        <div class="form__field sform__field--mobile-full" :class="{'has-error' : form.errors.has('phone_number') }">
                            <international-phone id="phone_number"
                                                 @validation="isValidPhoneNumber = $event"
                                                 name="phone_number"
                                                 v-model="form.phone_number"
                                                 class="phone_check phone-international"
                                                 placeholder="Phone Number">
                            </international-phone>
                            <span class="error" v-text="form.errors.phone_number"></span>
                        </div>
                    </div>

                    <div class="form__row" role="group">
                        <div class="form__field form__field--checkbox">
                            <input type="checkbox" id="text_notify" name="text_notify" v-model="form.text_notify">
                            <label for="text_notify">Get acceptance notification via text message.</label>
                        </div>
                    </div>

                    <div class="form__button">
                        <button type="button"
                                class="button"
                                :class="{'disabled' : stepOneDisabled }"
                                :disabled="form.isSubmitted"
                                @click="saveStepOne()" tabindex="0">
                            Get Started
                            <svg class="arrow-down-2"><use xlink:href="#arrow-down-2"></use></svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 2 -->
        <div class="step step--2 step--centered wrapper">
            <div class="step__inner membership-step">
                <div class="form">
                    <div class="app-membership">
                        <div class='app-membership__intro'>
                            <h2>Club Membership</h2>
                            <p>Membership has its perks (a lot of them). As a Well Traveled member you get access to…</p>
                        </div>
                        <membership-information></membership-information>
                        <div class='app-membership__price'>
                            <span v-text="`${trialDays} Day Free Trial`"></span>
                            <h4>
                                <p>Upon completion of free trial, membership is priced at {{ membershipInformation.price_text }}/year</p>
                            </h4>
                        </div>
                        <button type="button"
                                @click="saveStepTwo()"
                                class="button"
                                tabindex="0">
                            Next Step
                            <svg class="arrow-down-2"><use xlink:href="#arrow-down-2"></use></svg>
                        </button>
                        <div style="display: flex; justify-content: center" v-if="appFunnel" v-html="appFunnel.description"></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 3 -->
        <div class="step step--3 wrapper">
            <div class="step__inner">
                <div class="form">

                    <!-- Location, Date of Birth, Gender -->
                    <div class="form__section">

                        <div class="form__row">
                            <div class="form__row__right">
                                <div class="form__field form__field--small-margin">
                                    <span class="description description--center">* Required fields</span>
                                </div>
                            </div>
                        </div>

                        <!-- Location -->
                        <div class="form__row">
                            <div class="form__row__left">
                                <label for="location">Location*</label>
                            </div>
                            <div class="form__row__right">
                                <div class="form__field" :class="{'has-error' : form.errors.has('location')}">
                                    <input type="text"
                                           id="autocomplete-address"
                                           name="location"
                                           placeholder="Search For Your City"
                                           class="required"
                                           autocomplete="new-street-address">
                                    <span class="error" v-text="form.errors.city"></span>
                                </div>
                            </div>
                        </div>

                        <!-- Date of Birth -->
                        <div class="form__row" aria-labelledby="birthday_legend" role="group">
                            <div class="form__row__left">
                                <span class="legend" id="birthday_legend">Date of Birth*</span>
                            </div>
                            <div class="form__row__right">
                                <div class="form__row form__row--no-wrap">
                                    <div class="form__field form__field--quarter" :class="{'has-error' : form.errors.has('birthday_month' )}">
                                        <label for="birthday_month" class="accessible">Birthday Month</label>
                                        <input type="tel" id="birthday_month" name="birthday_month" v-model.number="form.birthday_month" placeholder="MM" class="month required">
                                        <span class="error" v-text="form.errors.birthday_month"></span>
                                    </div>
                                    <div class="form__field form__field--quarter" :class="{'has-error' : form.errors.has('birthday_day' )}">
                                        <label for="birthday_day" class="accessible">Birthday Day</label>
                                        <input type="tel" id="birthday_day" name="birthday_day" v-model.number="form.birthday_day" placeholder="DD" class="day required">
                                        <span class="error" v-text="form.errors.birthday_day"></span>
                                    </div>
                                    <div class="form__field form__field--half" :class="{'has-error' : form.errors.has('birthday_year' )}">
                                        <label for="birthday_year" class="accessible">Birthday Year</label>
                                        <input type="tel" id="birthday_year" name="birthday_year" v-model.number="form.birthday_year" data-minlength="4" placeholder="YYYY" class="year required">
                                        <span class="error" v-text="form.errors.birthday_year"></span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Gender -->
                        <div class="form__row">
                            <div class="form__row__left">
                                <label for="gender">Gender*</label>
                            </div>
                            <div class="form__row__right">
                                <div class="form__field form__field--select" :class="{'has-error' : form.errors.has('gender') }">
                                    <select id="gender" name="gender" class="required" v-model="form.gender"  placeholder="Gender">
                                        <option disabled selected :value="null">Select Your Gender</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </select>
                                    <span class="error" v-text="form.errors.gender"></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Directions -->
                    <div class="form__row">
                        <div class="form__row__right">
                            <div class="form__field">
                                <p>Please provide one of the below to help us get to know you (and confirm you're a real human).</p>
                            </div>
                        </div>
                    </div>

                    <!-- Social Media -->
                    <div class="form__row">
                        <div class="form__row__left">
                            <label for="social-media">Social Media</label>
                        </div>
                        <div class="form__row__right">
                            <div class="form__field form__field--select" :class="{'has-error' : form.errors.has('social_media')}">
                                <select id="social-media"
                                        name="social-media"
                                        class="social-select"
                                        v-model="form.social_media"
                                        placeholder="Select Your Platform">

                                    <option disabled selected :value="null">Select Your Platform</option>
                                    <option value="instagram">Instagram</option>
                                    <option value="tiktok">TikTok</option>
                                    <option value="linkedin">LinkedIn</option>
                                    <option value="website">Website</option>
                                </select>
                                <span class="error" v-text="form.errors.social_media"></span>
                            </div>
                        </div>
                    </div>

                    <!-- LinkedIn -->
                    <div class="form__row linkedin-row" v-if="form.social_media== 'linkedin'">
                        <div class="form__row__left">
                            <label for="linkedIn">LinkedIn</label>
                        </div>
                        <div class="form__row__right">
                            <div class="form__field" :class="{'has-error' : form.errors.has('linkedIn') }">
                                <input type="text"
                                       id="linkedIn"
                                       name="linkedIn"
                                       v-model="form.linkedIn"
                                       placeholder="Enter Your Profile URL"
                                       class="linkedInInstaWeb social-linkedIn">
                                <span class="description">Not sure where to find your LinkedIn profile link? <a href="https://www.linkedin.com/help/linkedin/answer/49315/finding-your-linkedin-public-profile-url?lang=en" target="_blank">Click here</a> for help.</span>
                                <span class="error" v-text="form.errors.linkedIn"></span>
                            </div>
                        </div>
                    </div>
                    <!-- Instagram -->
                    <div class="form__row instagram-row" v-if="form.social_media == 'instagram'">
                        <div class="form__row__left">
                            <label for="instagram">Instagram</label>
                        </div>
                        <div class="form__row__right">
                            <div class="form__field" :class="{'has-error' : form.errors.has('instagram') }">
                                <input type="text"
                                       id="instagram"
                                       name="instagram"
                                       v-model="form.instagram"
                                       placeholder="Enter Your Instagram Handle"
                                       class="linkedInInstaWeb social-instagram">
                                <span class="description">No need for the @ sign or the full URL, just your handle is good.</span>
                                <span class="error" v-text="form.errors.instagram"></span>
                            </div>
                        </div>
                    </div>
                    <!-- Tiktok -->
                    <div class="form__row instagram-row" v-if="form.social_media == 'tiktok'">
                        <div class="form__row__left">
                            <label for="instagram">TikTok</label>
                        </div>
                        <div class="form__row__right">
                            <div class="form__field" :class="{'has-error' : form.errors.has('tiktok') }">
                                <input type="text"
                                       id="tiktok"
                                       name="tiktok"
                                       v-model="form.tiktok"
                                       placeholder="Enter Your TikTok Handle"
                                       class="linkedInInstaWeb social-instagram">
                                <span class="description">No need for the @ sign or the full URL, just your handle is good.</span>
                                <span class="error" v-text="form.errors.tiktok"></span>
                            </div>
                        </div>
                    </div>
                    <!-- Website -->
                    <div class="form__row website-row" v-if="form.social_media == 'website'">
                        <div class="form__row__left">
                            <label for="website">Website</label>
                        </div>
                        <div class="form__row__right">
                            <div class="form__field" :class="{'has-error' : form.errors.has('website') }">
                                <input type="text"
                                       id="website"
                                       name="website"
                                       v-model="form.website"
                                       autocomplete="off"
                                       autocorrect="off"
                                       autocapitalize="off"
                                       spellcheck="false"
                                       placeholder="Enter Your Website URL"
                                       class="linkedInInstaWeb social-website">
                                <span class="error" v-text="form.errors.website"></span>
                            </div>
                        </div>
                    </div>

                    <div class="form__button">
                        <button type="button"
                                @click="saveStepThree()"
                                class="button"
                                :class="{'disabled' : stepThreeDisabled }"
                                :disabled="form.isSubmitted"
                                tabindex="0">
                            Next Step
                            <svg class="arrow-down-2"><use xlink:href="#arrow-down-2"></use></svg>
                        </button>
                    </div>

                </div>
            </div>
        </div>

        <!-- Step 4 -->
        <div class="step step--4 wrapper">
            <div class="step__inner">
                <div class="form">

                    <!-- Directions -->
                    <div class="form__row">
                        <div class="form__row__right">
                            <div class="form__field form__field--small-margin">
                                <span class="description description--center">* Required fields</span>
                            </div>
                        </div>
                    </div>

                    <!-- Next or recent trip -->
                    <div class="form__row">
                        <div class="form__row__left">
                            <label for="recent_or_next_trip">What are some of your favorite destinations and where are you off to next?*</label>
                        </div>
                        <div class="form__row__right">
                            <div class="form__field" :class="{'has-error' : form.errors.has('recent_or_next_trip')}">
                                <textarea id="recent_or_next_trip"
                                          rows="5"
                                          name="recent_or_next_trip"
                                          placeholder="Where did you go and what made it memorable? Where would you like to go next?"
                                          class="required"
                                          v-model="form.recent_or_next_trip">
                                </textarea>
                                <span class="error" v-text="form.errors.recent_or_next_trip"></span>
                            </div>
                        </div>
                    </div>

                    <!-- Joining the club -->
                    <div class="form__row">
                        <div class="form__row__left">
                            <label for="joining_the_club">Why are you interested in joining the club?*</label>
                        </div>
                        <div class="form__row__right">
                            <div class="form__field" :class="{'has-error': form.errors.has('joining_the_club')}">
                                <textarea id="joining_the_club"
                                          rows="4"
                                          name="joining_the_club"
                                          placeholder="Tell us what brought you here. Are you looking for travel inspo? Help planning a trip? Trusted recs? Good news, we’ve got all three."
                                          class="required"
                                          v-model="form.joining_the_club">
                                </textarea>
                                <span class="error" v-text="form.errors.joining_the_club"></span>
                            </div>
                        </div>
                    </div>

                    <div class="form__button">
                        <button type="button" @click="saveStepFour()"
                                class="button"
                                :class="{'disabled': stepFourDisabled }"
                                :disabled="form.isSubmitted"
                                tabindex="0">
                            Next Step
                            <svg class="arrow-down-2"><use xlink:href="#arrow-down-2"></use></svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 5 -->
        <div class="step step--5 wrapper">
            <div class="step__inner">
                <div class="form">

                    <div class="form__section">

                        <!-- How heard about us -->
                        <div id="js_referred_no">
                            <div class="form__row" aria-labelledby="how_hear" role="group">
                                <div class="form__row__left form__row__left--top">
                                    <span class="legend"
                                          :class="{'button-error' : form.errors.has('how_hear_about') }"
                                          id="how_hear">How’d You Hear About Us?* (Check All That Apply)</span>
                                </div>
                                <div class="form__row__right">
                                    <div v-for="option in howToHardOptions"
                                         class="form__field form__field--checkbox form__field--checkbox-required">
                                        <input type="checkbox"
                                               :id="`chk_${option.value}`"
                                               class="required how_hear_about"
                                               v-model="form.how_hear_about"
                                               :value="option.value">
                                        <label :for="`chk_${option.value}`" v-text="option.label"></label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Invite Code -->
                        <div v-if="form.how_hear_about.includes('invite_code')">
                            <div class="form__row">
                                <div class="form__row__left">
                                    <label for="invite_code_id">Invite Code*</label>
                                </div>
                                <div class="form__row__right">
                                    <div class="form__field" :class="{'has-error' : form.errors.has('invite_code_id') }">
                                        <input type="text"
                                               id="invite_code_id"
                                               name="invite_code_id"
                                               v-model="inviteCodeForm.invite_code"
                                               @blur="verifyInviteCode"
                                               @keyup="verifyInviteCode"
                                               placeholder="Enter Invite Code" >
                                        <span class="error"
                                              v-if="form.errors.has('invite_code_id')"
                                              v-text="form.errors.invite_code_id"></span>
                                        <span class="error"
                                              v-else-if="inviteCodeForm.errors.has('invite_code')"
                                              v-text="inviteCodeForm.errors.invite_code"></span>
                                    </div>
                                    <div v-if="inviteCode" style="margin-bottom: 1.25rem; margin-top: -1rem;">
                                        <p style="margin-bottom: 0" v-if="inviteCode.description" v-html="inviteCode.description"></p>
                                        <p style="margin-bottom: 0;" v-else>Invite Code Applied!</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- By Other Member -->
                        <div id="js_referred" v-if="form.how_hear_about.includes('wt_member')">
                            <div class="form__row">
                                <div class="form__row__left">
                                    <label for="referred_by">Referred By*</label>
                                </div>
                                <div class="form__row__right">
                                    <div class="form__field" :class="{'has-error' : form.errors.has('referred_by') }">
                                        <input type="text"
                                               id="referred_by"
                                               name="referred_by"
                                               v-model="form.referred_by"
                                               placeholder="Member Name" >
                                        <span class="error" v-text="form.errors.referred_by"></span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Other how heard about us -->
                        <div id="js_other_how_hear" v-if="form.how_hear_about.includes('other')">
                            <div class="form__row">
                                <div class="form__row__left">
                                    <label for="other_how_hear_text" class="">Other*</label>
                                </div>
                                <div class="form__row__right">
                                    <div class="form__field" :class="{'has-error' : form.errors.has('other_how_hear_text') }">
                                        <input type="text" id="other_how_hear_text"
                                               name="other_how_hear_text"
                                               v-model="form.other_how_hear_text"
                                               placeholder="Enter how you heard about us">
                                        <span class="error" v-text="form.errors.other_how_hear_text"></span>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    <!-- Terms and Conditions -->
                    <div class="form__row" aria-labelledby="terms_conditions" role="group">
                        <div class="form__row__left">
                            <span class="legend accessible" id="terms_conditions">Terms & Conditions</span>
                        </div>

                        <div class="form__row__right">
                            <div class="form__field form__field--checkbox" :class="{'has-error' : form.errors.has('legal') }">
                                <input type="checkbox"
                                       id="legal"
                                       name="legal"
                                       v-model="form.legal"
                                       class="required legal-checkbox">
                                <label for="legal" id="legal-info" :class="{'button-error' : form.errors.has('legal') }">
                                    By clicking "Apply" I acknowledge and agree to Well Traveled's <a href="/page/terms" target="_blank">Terms & Conditions</a> and <a href="/page/privacy-policy" target="_blank">Privacy Policy</a>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="form__row">
                        <div class="form__row__right">
                            <p class="description">If approved, your two week trial will begin immediately. Cancel any time during the trial and you will not be charged.</p>
                            <p class="description">Membership prices increase next month. Complete your application now to lock in an ongoing preferred rate.</p>
                        </div>
                    </div>

                    <div class="form__button">
                        <button type="submit"
                                class="button"
                                :class="{'disabled': stepFiveDisabled}"
                                :disabled="form.isSubmitted">
                            <span v-if="form.isSubmitted">Submitting...</span>
                            <span v-else>Apply</span>
                            <svg class="arrow-down-2"><use xlink:href="#arrow-down-2"></use></svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>

    </form>
</template>

<script>

import SignUpProcess from '@/mixins/SignUpProcess'
import axios from '@/util/axios'
import EmailValidator from "email-validator";
import InternationalPhone from '@/components/InternationalPhone'
import Helpers from '@/mixins/Helpers'
import MultiStep from "@/mixins/MultiStep"
import Form from "@/classes/Form"
import AutoCompleteAddress from '@/mixins/GooglePlaces';
import DateOfBirthClave from '@/mixins/DateOfBirthClave';
import MembershipInformation from "@/components/Pages/MembershipInformation"

export default {

    mixins: [SignUpProcess, MultiStep, Helpers, AutoCompleteAddress, DateOfBirthClave],

    props: {
        appFunnel : {
            type: [Object, null],
            required: false,
            default: null
        }
    },

    components:{
        InternationalPhone,
        MembershipInformation
    },

    data(){
        return {
            placeOptions: {
                types: ['(cities)'],
            },
            howToHardOptions: [
                {value: 'word_of_mouth', label: 'Word of Mouth'},
                {value: 'social_media', label: 'Social Media'},
                {value: 'podcast', label: 'Podcast'},
                {value: 'event', label: 'Event'},
                {value: 'search', label: 'Search'},
                {value: 'press', label: 'Press'},
                {value: 'wt_member', label: 'Well Traveled Member'},
                {value: 'invite_code', label: 'Invite Code'},
                {value: 'other', label: 'Other'},
            ],
            isValidPhoneNumber: false,
            totalSteps: 3,
            allowScrollIn: [2],
            inviteCode: null,
            inviteCodeForm: new Form({
                invite_code: '',
            }).dontClear(),
            form: new Form({
                // Base
                app_funnel_id: this.appFunnel ? this.appFunnel.id : null,
                invite_code_id: this.inviteCode ? this.inviteCode.id : null,

                // Step 1
                step: 'Introduction',
                text_notify: false,
                first_name: config.debug ? 'Frank' : '',
                last_name: config.debug ? 'Sepulveda' : '',
                email: config.debug ? 'francisco.sepulveda@distillery.com' : '',
                phone_number: config.debug ? '+15622535912' : '',
                // Step 3
                city:  config.debug ? 'La Paz' : '',
                region: config.debug ? 'BCS' : '',
                country: config.debug ? 'Mexico' : '',
                birthday_month: config.debug ? '12' : '',
                birthday_day:  config.debug ? '23' : '',
                birthday_year:  config.debug ? '1988' : '',
                gender:  config.debug ? 'male' : null,
                social_media:  config.debug ? 'instagram' : null,
                linkedIn: '',
                instagram: config.debug ? 'insta_frank' : '',
                website: '',
                tiktok: '',
                // Step 4
                recent_or_next_trip: config.debug ? 'Cabo San Lucas' : '',
                joining_the_club: config.debug ? 'Because I work here...' : '',
                // Step 5
                how_hear_about:  config.debug ?  [] : [],
                referred_by: null,
                other_how_hear_text: null,
                legal: true,
            })
        }
    },

    created() {
        this.addAbandonedListener()
        this.handleEnterKey()
        this.resolveEmailFromURL()
        eventBus.$on('step-change', this.saveAbandoned)
    },

    // mounted(){
    //     this.$nextTick(() => {
    //         this.changeStep(5)
    //     })
    // },

    methods: {

        saveAbandoned() {
            this.form.post(`/abandoned-funnel/application`, {
                clearForm: false
            })
        },

        submitMixPanelAbandoned(event) {
            event.preventDefault();
            this.saveAbandoned()
            this.form.post(`/abandoned-funnel/application-mixpanel`, {
                clearForm: false
            })
        },

        // Save the step 1 and move forward.
        saveStepOne() {
            this.validateEmailAddress()
            let fields = _.pick(this.form, [
                'step',
                'first_name',
                'email',
                'last_name',
                'phone_number',
                'text_notify',
                'app_funnel_id',
            ]);
            this.validate(fields).then(response => {
                if (this.form.phone_number.trim() != "" && !this.isValidPhoneNumber) {
                    this.form.errors.add("phone_number", "Please enter a valid phone number.")
                    return
                }
                this.form.step = 'Membership'
                this.setStepCompleted(1)
                this.changeStep(2)
            })
        },

        // Save the step 2 and move forward.
        saveStepTwo() {
            this.form.step = 'Your Details'
            this.setStepCompleted(2)
            this.changeStep(3);
        },

        // Save the step 3 and move forward.
        saveStepThree() {
            let fields = _.pick(this.form, [
                'step',
                'city',
                'region',
                'country',
                'birthday_month',
                'birthday_day',
                'birthday_year',
                'gender',
                'social_media',
                'linkedIn',
                'tiktok',
                'instagram',
                'website',
            ]);
            this.validate(fields).then(response => {
                this.form.step = 'Your Travel Style'
                this.setStepCompleted(3)
                this.changeStep(4);
            })
        },

        // Save the step 4 and move forward.
        saveStepFour() {
            let fields = _.pick(this.form, [
                'step',
                'recent_or_next_trip',
                'joining_the_club'
            ]);
            this.validate(fields).then(response => {
                this.form.step = 'Housekeeping'
                this.setStepCompleted(4)
                this.changeStep(5)
            })
        },

        // Save the step 5 and move forward.
        saveStepFive() {
            let fields = _.pick(this.form, [
                'step',
                'how_hear_about',
                'referred_by',
                'invite_code_id',
                'other_how_hear_text',
                'legal'
            ]);
            this.validate(fields).then(response => {
                this.setStepCompleted(4)
                this.submitApplication()
            })
        },

        // Save the application
        submitApplication() {
            this.form.post(`/apply`)
                .then(response => {
                    this.removeAbandonedListener()
                    this.redirectTo(`/application/complete?u=${response.user.id}`)
                })
        },


        // Make a request to validate the data.
        async validate(data) {
            this.form.errors.clear()
            return new Promise((resolve, rejected) => {
                axios.post(`/application/validate`, data)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        this.form.onFail(error.response.data)
                        this.addClearErrorEventListener()
                    })
            })
        },

        addClearErrorEventListener() {
            this.$nextTick(() => {
                $('.has-error').find(':input').each((index, input) => {
                    let listener = input.addEventListener('change', event => {
                        this.form.errors.clear(event.target.name)
                        input.removeEventListener('change', listener)
                    })
                })
            })
        },

        resolveEmailFromURL() {
            var url = new URL(window.location.href);
            var email = url.searchParams.get("e");
            if (email) {
                this.form.email = email
            }
        },

        verifyInviteCode: _.debounce(function() {
            this.inviteCodeForm.post(`/invite-code`)
                .then(response => {
                    this.inviteCode = response.invite_code
                    this.form.invite_code_id = this.inviteCode.id
                })
                .catch(response => {
                    this.inviteCode = null
                    this.form.invite_code_id = null
                })
        }, 500)
    },

    computed: {
        // Check if all required field are completed on the client side for the step 1.
        stepOneDisabled() {
            if (this.form.first_name.trim() == '') return true;
            if (this.form.last_name.trim() == '') return true;
            if (this.form.email.trim() == '' || !EmailValidator.validate(this.form.email)) return true;
            if(this.form.text_notify == true && this.isValidPhoneNumber == false) return true;
            return false
        },

        // Check if all required fields are completed on the client side for the step 2.
        stepThreeDisabled() {
            if (this.form.city.trim() == '') return true;
            if (this.form.region.trim() == '') return true;
            if (this.form.country.trim() == '') return true;
            if (this.form.birthday_day == '') return true;
            if (this.form.birthday_month == '') return true;
            if (this.form.birthday_year == '') return true;
            if (this.form.gender == null) return true;
            if (this.form.social_media == 'linkedin' && this.form.linkedIn.trim() == '') return true
            if (this.form.social_media == 'instagram' && this.form.instagram.trim() == '') return true
            if (this.form.social_media == 'tiktok' && this.form.tiktok.trim() == '') return true
            if (this.form.social_media == 'website' && this.form.website.trim() == '') return true
            return false
        },

        stepFourDisabled(){
            if (this.form.recent_or_next_trip.trim() == '') return true;
            if (this.form.joining_the_club.trim() == '') return true;
            return false
        },

        stepFiveDisabled(){
            if(this.form.how_hear_about.length == 0) return true;
            if(this.form.how_hear_about.includes('wt_member') && this.form.referred_by == '') return true;
            if(this.form.how_hear_about.includes('other') && this.form.other_how_hear_text == '') return true;
            if(this.form.isSubmitted) return true;
            return this.form.legal == false;
        },
    }
}
</script>
